import { useEffect, useRef, useState, useCallback } from 'react';

const SECTIONS = [
  { id: 'projects', label: 'Projects', hint: 'Selected work', icon: '▣' },
  { id: 'about', label: 'About', hint: 'Who I am', icon: '◉' },
  { id: 'skills', label: 'Skills', hint: 'Tech expertise', icon: '⬡' },
  { id: 'experience', label: 'Experience', hint: 'Where I\'ve worked', icon: '◈' },
  { id: 'education', label: 'Education', hint: 'Where I studied', icon: '◇' },
  { id: 'contact', label: 'Contact', hint: 'Get in touch', icon: '✉' },
];

function buildCommands(navigate) {
  return [
    ...SECTIONS.map(s => ({
      id: `go-${s.id}`,
      group: 'Navigate',
      label: `Go to ${s.label}`,
      hint: s.hint,
      icon: s.icon,
      keywords: `go ${s.id} ${s.label} ${s.hint}`,
      run: () => {
        if (window.location.pathname !== '/') {
          navigate('/');
          setTimeout(() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' }), 300);
        } else {
          document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' });
        }
      },
    })),
    {
      id: 'home',
      group: 'Navigate',
      label: 'Go home',
      hint: 'Top of page',
      icon: '⌂',
      keywords: 'home top hero start',
      run: () => {
        if (window.location.pathname !== '/') {
          navigate('/');
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
    },
    {
      id: 'resume',
      group: 'Navigate',
      label: 'Open resume',
      hint: 'View / download PDF',
      icon: '📄',
      keywords: 'resume cv pdf download',
      run: () => navigate('/resume'),
    },
    {
      id: 'github',
      group: 'Links',
      label: 'Open GitHub',
      hint: 'github.com/jrbauti19',
      icon: '⌥',
      keywords: 'github code source repo',
      run: () => window.open('https://github.com/jrbauti19', '_blank'),
    },
    {
      id: 'linkedin',
      group: 'Links',
      label: 'Open LinkedIn',
      hint: 'linkedin.com/in/joshua-raphael-bautista',
      icon: '⌥',
      keywords: 'linkedin social profile',
      run: () => window.open('https://www.linkedin.com/in/joshua-raphael-bautista-8a019a11b/', '_blank'),
    },
    {
      id: 'email',
      group: 'Links',
      label: 'Email me',
      hint: 'jrbauti19@gmail.com',
      icon: '✉',
      keywords: 'email contact mail reach',
      run: () => window.location.href = 'mailto:jrbauti19@gmail.com',
    },
    {
      id: 'whoami',
      group: 'Fun',
      label: 'whoami',
      hint: '→ Joshua Bautista · Full Stack Engineer',
      icon: '$',
      keywords: 'whoami about me name',
      run: () => {
        if (window.location.pathname !== '/') {
          navigate('/');
          setTimeout(() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' }), 300);
        } else {
          document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
        }
      },
    },
    {
      id: 'stats',
      group: 'Fun',
      label: 'stats',
      hint: '→ 100K+ users · 40% faster APIs · 85%+ test coverage',
      icon: '📊',
      keywords: 'stats metrics numbers performance',
      run: () => {
        if (window.location.pathname !== '/') {
          navigate('/');
          setTimeout(() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' }), 300);
        } else {
          document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
        }
      },
    },
    {
      id: 'konami',
      group: 'Fun',
      label: 'konami',
      hint: '↑↑↓↓←→←→ B A — try it',
      icon: '🎮',
      keywords: 'konami easter egg code game',
      run: () => {
        document.dispatchEvent(new CustomEvent('konami'));
      },
    },
    {
      id: 'theme',
      group: 'Preferences',
      label: 'Toggle theme',
      hint: 'Switch accent color',
      icon: '◐',
      keywords: 'theme color dark light mode toggle',
      run: () => {
        document.dispatchEvent(new CustomEvent('cycle-theme'));
      },
    },
    {
      id: 'help',
      group: 'Preferences',
      label: 'Keyboard shortcuts',
      hint: '⌘K palette · Esc close · ↑↓ navigate · ↵ select',
      icon: '?',
      keywords: 'help shortcuts keys keyboard',
      run: () => {},
    },
  ];
}

function fuzzyMatch(query, text) {
  if (!query) return true;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  if (t.includes(q)) return true;
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

function CommandPalette({ navigate }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const commands = buildCommands(navigate);

  const openPalette = useCallback(() => {
    setOpen(true);
    setQuery('');
    setActive(0);
  }, []);

  const closePalette = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(o => !o);
        return;
      }
      if (e.key === 'Escape' && open) {
        closePalette();
        return;
      }
      if (open) return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key.length === 1 && /[a-zA-Z0-9/]/.test(e.key)) {
        e.preventDefault();
        openPalette();
        setQuery(e.key);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, openPalette, closePalette]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQuery('');
    }
  }, [open]);

  const filtered = commands.filter(c => {
    if (!query) return true;
    return fuzzyMatch(query, c.label) || fuzzyMatch(query, c.keywords || '');
  });

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.children[active];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onSelect = cmd => {
    if (!cmd) return;
    closePalette();
    setTimeout(() => cmd.run(), 50);
  };

  const onListKey = e => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      onSelect(filtered[active]);
    }
  };

  let lastGroup = '';

  return (
    <>
      <div
        className={`cp-overlay${open ? ' open' : ''}`}
        onClick={closePalette}
        aria-hidden={!open}
      />
      <div
        className={`cp-panel${open ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <div className="cp-input-row">
          <span className="cp-icon">$</span>
          <input
            ref={inputRef}
            className="cp-input"
            placeholder="Type a command or search…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onListKey}
            spellCheck={false}
            autoComplete="off"
          />
          <kbd className="cp-esc">Esc</kbd>
        </div>
        {filtered.length === 0 ? (
          <div className="cp-empty">No matching commands</div>
        ) : (
          <div className="cp-list" ref={listRef}>
            {filtered.map((cmd, i) => {
              const showGroup = cmd.group !== lastGroup;
              lastGroup = cmd.group;
              return (
                <div key={cmd.id}>
                  {showGroup && <div className="cp-group">{cmd.group}</div>}
                  <button
                    className={`cp-item${i === active ? ' active' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => onSelect(cmd)}
                  >
                    <span className="cp-item-icon">{cmd.icon}</span>
                    <span className="cp-item-label">{cmd.label}</span>
                    <span className="cp-item-hint">{cmd.hint}</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div className="cp-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
          <span><kbd>↵</kbd> select</span>
          <span><kbd>Esc</kbd> close</span>
          <span className="cp-footer-spacer" />
          <span className="cp-footer-hint">{filtered.length} commands</span>
        </div>
      </div>
    </>
  );
}

export default CommandPalette;
